import React, { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'styled-components'
import {
  RESPONSIVE_BREAKPOINT_VERSIONS,
  useResponsiveBreakpointsVersion
} from 'contexts/ResponsiveBreakpointsContext'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'
import { Card, Header, TextRow } from '../Profile/Profile.styles'
import { Tonality } from '@dsny/dsny-component-library'
import { Arrowback, Musicnote, Info } from '@dsny/dsny-component-library/icons'
import {
  AccountDetailsColumn,
  AccountDetailsGrid,
  AccountDetailsRow,
  AccountManagementRoot,
  MusicHeroBanner,
  RequestMoreMusicContainer,
  RequestButton
} from './AccountManagement.styles'
import ModalActions from 'reducers/Modal'
import { REQUEST_MORE_MUSIC_URL } from 'utils/constants'

export const AccountManagement = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useDispatch()
  const isMobile = useMobileMediaQuery()
  const responsiveBreakpointsVersion = useResponsiveBreakpointsVersion()
  const responsiveBreakpointsV2 =
    responsiveBreakpointsVersion === RESPONSIVE_BREAKPOINT_VERSIONS.V2

  const handleBack = () => {
    window.history.back()
  }

  const openResetPasswordConfirmationModal = useCallback(() => {
    dispatch(ModalActions.toggleResetPasswordConfirmationModal())
  }, [])

  const openCancelAllSessionsModal = useCallback(() => {
    dispatch(ModalActions.toggleSignoutAllSessionsModal())
  }, [])

  return (
    <AccountManagementRoot
      $responsiveBreakpointsV2={responsiveBreakpointsV2}
      data-responsive-breakpoints={responsiveBreakpointsVersion}
    >
      <Header>
        <TextRow>
          <Tonality.UIHeading
            headingStyle="headline5"
            color={theme.colors.neutralW100}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            {isMobile && (
              <Arrowback
                width={20}
                height={20}
                color={theme.colors.neutralW100}
                style={{ marginRight: 10 }}
                onClick={handleBack}
              />
            )}

            {t('settings.account-management')}
          </Tonality.UIHeading>

          <Tonality.UIText
            textStyle="body"
            color={theme.colors.neutralW100}
            style={{ fontSize: 14 }}
          >
            {t('settings.account-management-subtitle')}
          </Tonality.UIText>
        </TextRow>
      </Header>

      <Card>
        <AccountDetailsGrid>
          <AccountDetailsRow>
            <AccountDetailsColumn>
              <Tonality.UIText
                textStyle="bodyBold"
                color={theme.colors.neutralW100}
                style={{ fontSize: 14, marginBottom: 4 }}
              >
                {t('settings.subscription')}
              </Tonality.UIText>
            </AccountDetailsColumn>

            <AccountDetailsColumn>
              <Tonality.UIText
                textStyle="body"
                color={theme.colors.neutralW100}
                style={{
                  fontSize: 14,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                {t('settings.no-active-subscription')}
                <Tonality.Tooltip
                  mode="dark"
                  hhasArrow={false}
                  placement="top"
                  content={t('settings.subscription-info')}
                  containerStyles={{ width: 360 }}
                  children={
                    <span>
                      <Info width={16} height={16} />
                    </span>
                  }
                />
              </Tonality.UIText>
            </AccountDetailsColumn>
          </AccountDetailsRow>

          <AccountDetailsRow>
            <AccountDetailsColumn>
              <Tonality.UIText
                textStyle="bodyBold"
                color={theme.colors.neutralW100}
                style={{ fontSize: 14, marginBottom: 4 }}
              >
                {t('login.password')}
              </Tonality.UIText>
            </AccountDetailsColumn>

            <AccountDetailsColumn>
              <Tonality.UIText
                textStyle="body"
                color={theme.colors.player50}
                style={{ fontSize: 14, cursor: 'pointer' }}
                onClick={openResetPasswordConfirmationModal}
              >
                {t('settings.change-password')}
              </Tonality.UIText>
            </AccountDetailsColumn>
          </AccountDetailsRow>

          <AccountDetailsRow>
            <AccountDetailsColumn>
              <Tonality.UIText
                textStyle="bodyBold"
                color={theme.colors.neutralW100}
                style={{ fontSize: 14 }}
              >
                {t('settings.signout-everywhere')}
              </Tonality.UIText>
            </AccountDetailsColumn>

            <AccountDetailsColumn>
              <Tonality.UIText
                textStyle="body"
                color={theme.colors.player50}
                style={{ fontSize: 14, cursor: 'pointer' }}
                onClick={openCancelAllSessionsModal}
              >
                {t('settings.signout-everywhere')}
              </Tonality.UIText>
            </AccountDetailsColumn>
          </AccountDetailsRow>
        </AccountDetailsGrid>
      </Card>

      <MusicHeroBanner>
        <Musicnote width={26} height={26} color={theme.colors.white0} />
        <Tonality.UIHeading
          headingStyle="headline4"
          color={theme.colors.neutralW100}
          style={{ fontSize: 24 }}
        >
          {t('settings.want-more-music')}
        </Tonality.UIHeading>

        <Musicnote width={26} height={26} color={theme.colors.white0} />
      </MusicHeroBanner>

      <RequestMoreMusicContainer>
        <Tonality.UIText
          textStyle="body"
          color={theme.colors.neutralW100}
          style={{ fontSize: 16, fontWeight: 400 }}
        >
          {t('settings.want-more-music-body')}
        </Tonality.UIText>

        <RequestButton
          variant="primary"
          setting="player"
          mode="dark"
          onClick={() => window.open(REQUEST_MORE_MUSIC_URL, '_blank')}
        >
          {t('settings.request-new-content')}
        </RequestButton>
      </RequestMoreMusicContainer>
    </AccountManagementRoot>
  )
}

export default AccountManagement
